import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, BrainCircuit, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { forgotPassword } from "@/lib/api";

// Temporarily hide "Continue with Google" UI while the Emergent-managed OAuth
// consent-screen app name ("Www") is corrected to "CoreText". UI-only switch —
// the underlying Google OAuth integration (google() + AuthCallback) is untouched.
// Set to false to restore the button.
const GOOGLE_LOGIN_HIDDEN = true;


function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agree) {
      toast.error("Please accept the Terms of Service and Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(email);
        toast.success("If an account exists for that email, a reset link is on its way. Check your inbox.");
        setMode("login");
      } else if (mode === "register") {
        await register(email, password, name, agree);
        toast.success("Account created. Your intelligence is now saved.");
        navigate("/history");
      } else {
        await login(email, password);
        toast.success("Welcome back.");
        navigate("/history");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/history";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-5 py-16 text-[var(--text)]">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#05060a]/40 via-transparent to-[#05060a]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md glass neon-border rounded-2xl p-8 scanlines"
        data-testid="auth-card"
      >
        <Link to="/" className="flex items-center gap-2.5 mb-7 w-fit" data-testid="auth-logo">
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg border border-neon-cyan/50 glow-cyan">
            <BrainCircuit size={20} className="text-neon-cyan" strokeWidth={2} />
          </span>
          <span className="font-display font-bold text-lg tracking-[0.18em] text-white text-glow-cyan">
            Coretext
          </span>
        </Link>

        <p className="font-mono text-[10px] tracking-[0.3em] text-neon-amber mb-2">
          {mode === "register" ? "CREATE YOUR ACCOUNT" : mode === "forgot" ? "RESET PASSWORD" : "SIGN IN"}
        </p>
        <h1 className="font-display text-3xl font-bold tracking-tight leading-none mb-2 text-white">
          {mode === "register" ? "Save your build intelligence." : mode === "forgot" ? "Forgot your password?" : "Welcome back."}
        </h1>
        <p className="font-mono text-sm text-[var(--muted)] mb-6 leading-relaxed">
          {mode === "register"
            ? "Track your progress build over build and watch yourself get sharper at vibecoding."
            : mode === "forgot"
            ? "Enter your email and we'll send you a secure link to set a new password. It expires in 30 minutes."
            : "Pick up your history, trends, and personal burn rate."}
        </p>

        {/* Google login temporarily hidden pending correction of the Emergent-managed OAuth consent-screen app name ("Www" → "CoreText"). Underlying OAuth integration (google() + AuthCallback) is preserved. To restore, set GOOGLE_LOGIN_HIDDEN = false. */}
        {!GOOGLE_LOGIN_HIDDEN && mode !== "forgot" && (
          <>
            <button
              type="button"
              onClick={google}
              data-testid="google-signin-button"
              className="w-full flex items-center justify-center gap-3 rounded-lg border border-neon-cyan/30 bg-[#0b0d14] py-3 font-display font-semibold text-white hover:border-neon-cyan hover:glow-cyan transition-colors transition-shadow duration-200"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
              Continue with Google
            </button>

            <p className="font-mono text-[10px] text-[var(--muted)] mt-2.5 text-center leading-relaxed">
              By continuing with Google you agree to our{" "}
              <Link to="/terms" target="_blank" className="text-neon-cyan hover:underline">Terms</Link> &amp;{" "}
              <Link to="/privacy" target="_blank" className="text-neon-cyan hover:underline">Privacy Policy</Link>.
            </p>

            <div className="flex items-center gap-3 my-5">
              <span className="h-px flex-1 bg-[var(--line)]" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--muted)]">OR</span>
              <span className="h-px flex-1 bg-[var(--line)]" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                data-testid="auth-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name (optional)"
                className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg pl-10 pr-4 py-3 font-mono text-sm text-white placeholder:text-[var(--muted)] focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors"
              />
            </div>
          )}
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@startup.com"
              className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg pl-10 pr-4 py-3 font-mono text-sm text-white placeholder:text-[var(--muted)] focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors"
            />
          </div>
          {mode !== "forgot" && (
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                data-testid="auth-password-input"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "Create a password (min 8 chars)" : "Password"}
                className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg pl-10 pr-11 py-3 font-mono text-sm text-white placeholder:text-[var(--muted)] focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors"
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[var(--muted)] hover:text-neon-cyan hover:bg-neon-cyan/10 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          )}
          {mode === "login" && (
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => setMode("forgot")}
                className="font-mono text-[11px] text-[var(--muted)] hover:text-neon-cyan transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}
          {mode === "register" && (
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="auth-terms">
              <input
                type="checkbox"
                data-testid="auth-terms-checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 accent-[#22d3ee] w-4 h-4 shrink-0"
              />
              <span className="font-mono text-[11px] text-[var(--muted)] leading-relaxed">
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-neon-cyan hover:underline">Terms of Service</Link>{" "}
                and acknowledge the{" "}
                <Link to="/privacy" target="_blank" className="text-neon-cyan hover:underline">Privacy Policy</Link>.
              </span>
            </label>
          )}
          <button
            data-testid="auth-submit-button"
            type="submit"
            disabled={loading}
            style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
            className="w-full rounded-lg font-display font-bold tracking-wide py-3 flex items-center justify-center gap-2 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200 disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> PLEASE WAIT...</>
            ) : (
              <>
                {mode === "register" ? "CREATE ACCOUNT" : mode === "forgot" ? "SEND RESET LINK" : "SIGN IN"}{" "}
                <ArrowRight size={18} strokeWidth={2.5} />
              </>
            )}
          </button>
        </form>

        <p className="font-mono text-xs text-[var(--muted)] mt-5 text-center">
          {mode === "forgot" ? (
            <button
              type="button"
              data-testid="auth-back-to-login"
              onClick={() => setMode("login")}
              className="text-neon-cyan hover:underline"
            >
              ← Back to sign in
            </button>
          ) : (
            <>
              {mode === "register" ? "Already have an account?" : "New to Coretext?"}{" "}
              <button
                type="button"
                data-testid="auth-toggle-mode"
                onClick={() => setMode(mode === "register" ? "login" : "register")}
                className="text-neon-cyan hover:underline"
              >
                {mode === "register" ? "Sign in" : "Create a free account"}
              </button>
            </>
          )}
        </p>
      </motion.div>
    </div>
  );
}
